import { API_BASE_URL, ENDPOINTS, REQUEST_CONFIG } from './chatConfig';

/**
 * API методы для работы с заказами (Orders)
 */

/**
 * Создание нового заказа
 * @param {Object} orderData - Данные заказа
 * @param {string} orderData.email - Email пользователя
 * @param {string} orderData.phone - Телефон пользователя
 * @param {string} orderData.country - Страна (2-буквенный код)
 * @param {string} orderData.state - Штат/регион
 * @returns {Promise<Object>} Созданный заказ
 */
export const createOrder = async (orderData) => {
  try {
    const response = await fetch(`${API_BASE_URL}${ENDPOINTS.ORDERS}`, {
      method: 'POST',
      ...REQUEST_CONFIG,
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Обновление заказа с тарифным планом
 * @param {string} orderId - ID заказа
 * @param {Object} tariffData - Данные тарифа
 * @param {number} tariffData.amount - Сумма
 * @param {string} tariffData.promoCode - Промокод (опционально)
 * @returns {Promise<Object>} Обновленный заказ
 */
export const updateOrderWithTariff = async (orderId, tariffData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/tariff`, {
      method: 'PATCH',
      ...REQUEST_CONFIG,
      body: JSON.stringify(tariffData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating order with tariff:', error);
    throw error;
  }
};

/**
 * Получение заказа по ID
 * @param {string} orderId - ID заказа
 * @returns {Promise<Object>} Данные заказа
 */
export const getOrderById = async (orderId) => {
  try {
    const response = await fetch(`${API_BASE_URL}${ENDPOINTS.ORDER_BY_ID(orderId)}`, {
      method: 'GET',
      ...REQUEST_CONFIG,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching order by ID:', error);
    throw error;
  }
};

/**
 * Поиск заказов с фильтрами
 * @param {Object} filters - Фильтры поиска
 * @param {string} filters.email - Email для поиска
 * @param {string} filters.status - Статус заказа (PENDING, PAID, CANCELLED, REFUNDED)
 * @param {string} filters.country - Страна (2-буквенный код)
 * @param {string} filters.promoCode - Промокод
 * @param {number} filters.page - Номер страницы (по умолчанию 1)
 * @param {number} filters.limit - Количество на странице (по умолчанию 20)
 * @returns {Promise<Object>} Список заказов и пагинация
 */
export const searchOrders = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.email) queryParams.append('email', filters.email);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.country) queryParams.append('country', filters.country);
    if (filters.promoCode) queryParams.append('promoCode', filters.promoCode);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);

    const url = `${API_BASE_URL}${ENDPOINTS.ORDERS}?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      ...REQUEST_CONFIG,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      orders: data.data,
      pagination: data.pagination,
    };
  } catch (error) {
    console.error('Error searching orders:', error);
    throw error;
  }
};

/**
 * Обновление статуса заказа
 * @param {string} orderId - ID заказа
 * @param {Object} statusData - Данные статуса
 * @param {string} statusData.status - Новый статус (PAID, CANCELLED, REFUNDED)
 * @param {string} statusData.paymentId - ID платежа (опционально)
 * @returns {Promise<Object>} Результат обновления
 */
export const updateOrderStatus = async (orderId, statusData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
      method: 'PATCH',
      ...REQUEST_CONFIG,
      body: JSON.stringify(statusData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

/**
 * Получение статистики заказов (для админов)
 * @returns {Promise<Object>} Статистика заказов
 */
export const getOrderStatistics = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/admin/stats`, {
      method: 'GET',
      ...REQUEST_CONFIG,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching order statistics:', error);
    throw error;
  }
};

/**
 * Поиск заказов по email (упрощенный метод)
 * @param {string} email - Email пользователя
 * @returns {Promise<Array>} Список заказов пользователя
 */
export const getOrdersByEmail = async (email) => {
  try {
    const result = await searchOrders({ email, limit: 100 });
    return result.orders;
  } catch (error) {
    console.error('Error fetching orders by email:', error);
    throw error;
  }
};
